import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { useMe } from "@/lib/hooks/use-me";

export function useMyCompletedShifts() {
  const { me } = useMe();
  const ITEMS_PER_PAGE = 10;

  const { data, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery({
    queryKey: ["shifts", "completed", me?.employee?.id, ITEMS_PER_PAGE],
    queryFn: async (args) => {
      const res = await api.shift.getAll({
        pagination: {
          take: ITEMS_PER_PAGE,
          page: args.pageParam + 1,
        },
        sort: {
          field: "endAt",
          order: "desc",
        },
        filter: {
          employeeId: {
            value: me?.employee?.id,
            operator: "equals",
          },
          // A shift counts as "completed" (afgerond) once its scheduled end
          // time has passed — nothing else. We deliberately do NOT treat the
          // presence of a shiftTimeEntry as completed: a planner can enter or
          // prefill worked hours days in advance, and that must never flip a
          // still-upcoming shift into the completed list. This mirrors the
          // upcoming query (use-my-shifts.ts, endAt >= now), so the two tabs
          // stay perfectly complementary with no gap or overlap.
          endAt: {
            value: new Date().toISOString(),
            operator: "lt",
          },
        },
        include: {
          project: {
            select: true,
          },
          position: {
            select: true,
          },
          userFunction: {
            select: true,
          },
          shiftTimeEntry: {
            select: true,
          },
        },
        aggregate: {
          cancellationRequest: {
            compute: true,
          },
        },
      });
      return res.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const totalItems = lastPage.totalAmount;
      const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

      if (currentItems >= totalItems) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      return firstPageParam - 1;
    },
    enabled: !!me?.employee?.id,
  });

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  const shifts =
    data?.pages.flatMap((page) => page.items).filter((shift) => shift !== undefined) ?? [];

  return {
    shifts,
    loadMore,
    hasNextPage,
    refetch,
  };
}
